/**
 * Favorites API
 * Handles CRUD for saved favorites (filters, group-by, search) per page
 */
import apiClient from './apiClient';

export interface Favorite {
  id: number;
  user_id: number;
  page_key: string;
  name: string;
  config: FavoriteConfig;
  is_default: boolean;
  shared: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface FavoriteConfig {
  search?: string;
  column_searches?: Array<{ field: string; label: string; value: string }>;
  group_by?: string[];
  filters?: Record<string, string | number | boolean | null>;
  visible_columns?: string[];
}

interface FavoritesListResponse {
  favorites: Favorite[];
}

interface FavoriteResponse {
  favorite: Favorite;
}

export const favoritesApi = {
  /** List favorites for a page (own + shared) */
  async getFavorites(pageKey: string): Promise<Favorite[]> {
    const res = await apiClient.get<FavoritesListResponse>(
      `/favorites?page_key=${encodeURIComponent(pageKey)}`
    );
    return res.favorites;
  },

  /** Create a new favorite */
  async createFavorite(data: {
    page_key: string;
    name: string;
    config: FavoriteConfig;
    is_default?: boolean;
    shared?: boolean;
  }): Promise<Favorite> {
    const res = await apiClient.post<FavoriteResponse>('/favorites', data);
    return res.favorite;
  },

  /** Update an existing favorite */
  async updateFavorite(
    id: number,
    data: {
      name?: string;
      config?: FavoriteConfig;
      is_default?: boolean;
      shared?: boolean;
    }
  ): Promise<Favorite> {
    const res = await apiClient.put<FavoriteResponse>(`/favorites/${id}`, data);
    return res.favorite;
  },

  /** Delete a favorite */
  async deleteFavorite(id: number): Promise<void> {
    await apiClient.delete(`/favorites/${id}`);
  },
};
